import React from "react";
import styled from "@emotion/styled";
import Carousel from "react-multi-carousel";
import { Box, Flex, Text, Heading } from "@theme-ui/components";
import { useBreakpointIndex } from "@theme-ui/match-media";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import { colors, typography } from "../../styles/theme";
import bullet from "../../../static/images/bullet-icon.png";
import { DisplayIcon } from "../atoms";

const ModuleSection = ({ moduleSectionHeading, modules }) => {
  const responsive = {
    allScreens: {
      breakpoint: { max: 4000, min: 0 },
      items: 1,
    },
  };
  const bpIndex = useBreakpointIndex({ defaultIndex: 1 });

  const CustomDot = ({ index, onClick, active }) => {
    const getLabel = modules.map(({ title }) => title);
    const getIcon = modules.map(({ iconName }) => iconName);
		    return (
      <Flex
        sx={{
          backgroundColor: active ? colors.primary100 : "transparent",
          ...styles.customDotsStyles,
          color: active ? colors.primaryMain : colors.neutral900,
        }}
        onClick={onClick}
      >
        <DisplayIcon
          theme={"primary"}
          iconName={React.Children.toArray(getIcon)[index]}
          iconColor={colors.white}
					pointerCursor
        />
        <Text
					          dangerouslySetInnerHTML={{
            __html: React.Children.toArray(getLabel)[index],
          }}
        />
      </Flex>
    );
  };

  return (
    <Flex sx={{ flexDirection: "column", justifyContent: "center" }}>
      <Heading
        as="h2"
        sx={{
          ...typography.h2,
          textAlign: "center",
          p: { m: 0 },
          mb: ["3rem", "3rem", "3rem", "4rem"],
        }}
        dangerouslySetInnerHTML={{ __html: moduleSectionHeading }}
      />
      <Box
        sx={{
          ...styles.carouselWrapper,
          ...styles.customDotWrapper,
          ...styles.commonStyles,
        }}
      >
        <Carousel
          responsive={responsive}
          arrows={false}
          showDots
          customDot={bpIndex > 2 ? <CustomDot /> : null}
          renderDotsOutside={true}
          additionalTransfrom={0}
          infinite
          autoPlay={true}
          autoPlaySpeed={3500}
          keyBoardControl
          minimumTouchDrag={80}
          slidesToSlide={1}
          renderButtonGroupOutside={true}
          customTransition="opacity 5s ease-in"
          transitionDuration={1000}
          containerClass="carousel-container"
        >
          {modules.length > 0 &&
            modules.map(({ id, title, advantages, image }) => (
              <Flex key={id} sx={styles.moduleSection}>
                <Flex
                  sx={{
                    width: ["100%", "100%", "100%", "50%"],
                    justifyContent: [
                      "center",
                      "center",
                      "flex-end",
                      "flex-end",
                    ],
                  }}
                >
                  <GatsbyImage image={getImage(image)} alt={image?.alt} />
                </Flex>
                <Box
                  sx={{
                    textAlign: "left",
                    width: ["100%", "100%", "100%", "50%"],
                    px: ["2rem", "2rem", 0, 0],
                  }}
                >
                  <Box
                    sx={{
                      ...typography.h3,
                      color: colors.primary700,
                      p: { m: 0 },
                      mb: "1.5rem",
                    }}
                    dangerouslySetInnerHTML={{ __html: title }}
                  />
                  <StyledUL>
                    {advantages?.length > 0 &&
                      advantages.map(({ id, desc, desc1 }) => (
                        <Box key={id}>
                          {desc && (
                            <li>
                              <Text
                                sx={typography.textMd400}
                                dangerouslySetInnerHTML={{ __html: desc }}
                              />
                            </li>
                          )}
                          {desc1 && (
                            <Text
                            as='a'
                              sx={{
                                a: {
                                  ...typography.h6,
                                  mt: "1.5rem",
                                  color: colors.primaryMain,
                                  textDecoration: "none",
                                  "&:hover": {
                                    textDecoration: "underline",
                                  },
                                },
                              }}
                              dangerouslySetInnerHTML={{ __html: desc1 }}
                            />
                          )}
                        </Box>
                      ))}
                  </StyledUL>
                </Box>
              </Flex>
            ))}
        </Carousel>
      </Box>
    </Flex>
  );
};

const StyledUL = styled.ul`
	padding-left: 0px;
	li {
		padding-left: 2.25rem;
		list-style: none;
		background-image: url(${bullet});
		background-repeat: no-repeat;
		background-position: left 4px;
		background-size: 18px;
		vertical-align: middle;
    padding-bottom:0.75rem;
	}
	.sub-ul {
		padding: 1.5rem
		background-image: none !important;
		li {
			background-image: none !important;
			background-size: 0px;
			padding-left: 0px;
			p {
				display: inline;
			}
		}
	}
`;

const styles = {
  moduleSection: {
    height: "100%",
    justifyContent: ["flex-start", "flex-start", "flex-start", "center"],
    alignItems: "center",
    flexDirection: ["column", "column", "row", "row"],
    gap: ["1.5rem", "1.5rem", "1.5rem", "4.18rem"],
  },
  customDotsStyles: {
    ...typography.textMd500,
    flexDirection: "column",
    gap: "1rem",
    justifyContent: "flex-start",
    alignItems: "center",
    width: "13.25rem",
    height: "11.5rem",
    borderRadius: "1.25rem",
    padding: "1.75rem 1rem 1rem 1rem",
    mr: "4px",
    "&:hover": {
			      color: colors.primaryMain,
      backgroundColor: colors.primary100,
    },
    cursor: "pointer",
  },
  commonStyles: {
    ".carousel-container": {
      width: "100%",
      p: 0,
    },
  },
  carouselWrapper: {
    width: "100%",

    ".react-multi-carousel-dot-list": {
      position: "relative !important",
      bottom: ["-2rem", "-2rem", "-2rem", "-4rem"],
    },
    ".react-multi-carousel-dot": {
      button: {
        border: "none",
        backgroundColor: colors.neutral200,
        "&:hover": {
          backgroundColor: colors.primary100,
        },
        "&:focus": {
          outline: "none",
        },
      },
    },
    ".react-multi-carousel-dot--active": {
      button: {
        backgroundColor: colors.primaryMain,
      },
    },
  },
  customDotWrapper: {
    position: "relative",
  },
};

export default React.memo(ModuleSection);
